<template>
  <div ref="root" class="app-actions-menu" @keydown.esc.stop.prevent="close(true)" @focusout="leave">
    <app-button ref="toggle" theme="empty" class="app-actions-menu__toggle" :disabled="disabled" :aria-expanded="String(open)" :aria-controls="menuId" aria-haspopup="menu" @click.native="toggle" @keydown.native.down.prevent="show">
      <i class="pi pi-bars" aria-hidden="true" /> Actions <i class="pi pi-chevron-down" aria-hidden="true" />
    </app-button>
    <div v-if="open" :id="menuId" ref="menu" class="app-actions-menu__panel" role="menu" :aria-label="title" tabindex="-1" @keydown="navigate">
      <p class="app-actions-menu__title">{{ title }}</p>
      <button v-for="action in actions" :key="action.code" type="button" role="menuitem" class="app-actions-menu__item" :class="`app-actions-menu__item--${action.tone || 'default'}`" :disabled="Boolean(action.disabled)" @click="select(action)">
        <i class="pi" :class="action.icon" aria-hidden="true" />
        <span><strong>{{ action.name }}</strong><span class="app-actions-menu__description">{{ action.description }}</span><span v-if="action.disabledReason && action.disabledReason !== action.description" class="app-actions-menu__description">{{ action.disabledReason }}</span></span>
      </button>
      <p v-if="!actions.length" class="app-actions-menu__empty">No actions available.</p>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AppActionsMenu',
  props: {
    actions: { type: Array, default: () => ([]) },
    title: { type: String, default: 'Booking actions' },
    disabled: { type: Boolean, default: false }
  },
  data() { return { open: false } },
  computed: { menuId() { return `app-actions-menu-${this._uid}` } },
  watch: {
    disabled(value) { if (value) this.close() }
  },
  mounted() { document.addEventListener('click', this.outside) },
  beforeDestroy() { document.removeEventListener('click', this.outside) },
  methods: {
    toggle() {
      if (this.open) this.close()
      else this.show()
    },
    async show() {
      if (this.disabled) return
      this.open = true
      await this.$nextTick()
      if (this.$refs.menu) (this.$refs.menu.querySelector('button:not(:disabled)') || this.$refs.menu).focus()
    },
    close(focus = false) {
      this.open = false
      if (focus) this.focusToggle()
    },
    focusToggle() { if (this.$refs.toggle) this.$refs.toggle.$el.focus() },
    outside(event) { if (!this.$refs.root.contains(event.target)) this.close() },
    leave(event) { if (event.relatedTarget && !this.$refs.root.contains(event.relatedTarget)) this.close() },
    select(action) {
      if (action.disabled || this.disabled) return
      this.close()
      this.$emit('select', action)
    },
    navigate(event) {
      if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return
      event.preventDefault()
      const items = Array.from(this.$refs.menu.querySelectorAll('button:not(:disabled)'))
      if (!items.length) return
      const index = items.indexOf(document.activeElement)
      const next = event.key === 'Home' ? 0 : event.key === 'End' ? items.length - 1 : (index + (event.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length
      items[next].focus()
    }
  }
}
</script>

<style lang="scss" scoped>
.app-actions-menu { position: relative; flex-shrink: 0; font-family: $font-family-base; }
.app-actions-menu .app-actions-menu__toggle { min-width: auto; min-height: 34px; padding: 6px 10px; border: 1px solid #dce0d6; border-radius: 6px; background: #fff; color: #20241d; font-size: 13px; line-height: 1.4; gap: 8px; }
.app-actions-menu .app-actions-menu__toggle:hover { background: #fafafa; }
.app-actions-menu .app-actions-menu__toggle:disabled { opacity: .6; }
.app-actions-menu__toggle .pi { font-size: 13px; }
.app-actions-menu__panel { position: absolute; right: 0; top: calc(100% + 8px); z-index: 20; width: 320px; max-width: calc(100vw - 40px); padding: 6px; border: 1px solid $color-border; border-radius: 9px; background: #fff; box-shadow: 0 8px 24px rgba(0, 0, 0, .12); }
.app-actions-menu__title, .app-actions-menu__empty { margin: 0; padding: 10px 12px; color: $color-text-second; font-size: 14px; }
.app-actions-menu__item { display: flex; align-items: flex-start; gap: 12px; width: 100%; padding: 12px; border: 0; border-radius: 6px; background: #fff; color: $color-text-base; text-align: left; font: inherit; font-size: 14px; line-height: 1.45; cursor: pointer; }
.app-actions-menu__item > .pi { flex-shrink: 0; margin-top: 4px; font-size: 14px; }
.app-actions-menu__item > span { min-width: 0; }
.app-actions-menu__item strong { display: block; font-weight: 500; }
.app-actions-menu__item--danger { color: #a33b35; }
.app-actions-menu__description { display: block; margin-top: 4px; color: $color-text-second; font-weight: 400; overflow-wrap: anywhere; }
.app-actions-menu__item:hover:not(:disabled) { background: #f7f7f7; }
.app-actions-menu__item:disabled { background: #f3f4f1; color: #a5a99f; opacity: 1; cursor: not-allowed; }
.app-actions-menu__item:disabled .app-actions-menu__description { color: inherit; }
.app-actions-menu__item:focus-visible, .app-actions-menu__toggle:focus-visible { outline: 2px solid $color-theme; outline-offset: 2px; }
@media (max-width: 760px) { .app-actions-menu__panel { left: 0; right: auto; width: 280px; } }
</style>
